import React, { useEffect, useMemo, useState } from 'react'
import type { Paragraph, TextRun } from '@/api/types/stowed-element'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { useFontsOptional } from '@/providers/font'
import {
  type RowMetricPt,
  type ShapeTextFlow,
  type ShapeTextLayoutResult,
  applyPerLineLeadingToFlow,
  layoutShapeTextWithPerLineLeading,
  paragraphsToPlainText,
} from '@/lib/shapes/shape-text'
import { getShapeTextFlowBoundary } from '@/lib/boundaries/text-boundary'
import { type DropCapBox, layoutShapeTextWithDropCap } from '@/lib/shapes/shape-drop-cap'
import { buildShapeTextMeasurer, getSharedMeasureContext } from '@/lib/shapes/shape-text-measure'
import type { ShapeTextWrapContext } from '@/lib/shapes/types/shape-text-wrap-context'
import { shapeTextFlowWithWrap } from '@/lib/shapes/shape-text-wrap-bands'

interface UseShapeTextLayoutOptions {
  shape: ShapeData
  paragraphs: Paragraph[]
  isEditing: boolean
  /**
   * Whether this shape has a next frame in its text chain — which lets the layout break the trailing
   * word at capacity instead of deferring it whole (`breakTrailingWordAtCapacity`).
   *
   * **Not cosmetic: this is caret correctness.** `shape-text-editor.tsx` derives the chain hand-off
   * offset as `selectionStart - layout.consumedLength`, and `home.tsx`'s
   * `handleShapeChainOverflowCaret` places that offset in the next frame — which was split at
   * `fitParagraphsToShape`'s own `consumedLength`. If the live layout and the fitter disagree on the
   * word policy, the caret lands off by exactly the characters the fitter kept.
   */
  hasNextInChain?: boolean
  /**
   * Text-wrap context — nearby shapes plus the unified z-order. Omit and this frame renders with no
   * exclusion zones.
   *
   * **Must be the same context `fitParagraphsToShape` is given** for this shape, or measurement and
   * paint disagree and a chain seam drifts — the same class of hazard `hasNextInChain` documents
   * above. See `shapeTextFlowWithWrap`.
   */
  wrap?: ShapeTextWrapContext
}

export interface UseShapeTextLayoutResult {
  textValue: string
  setTextValue: React.Dispatch<React.SetStateAction<string>>
  charRunMap: Array<TextRun | null>
  /** Narrowed for any active Inside drop cap's own rows beyond its first — see
   *  `DropCapLayoutResult.flow`. Anything that paints or hit-tests row geometry must use this, not
   *  a flow it derives itself. */
  flow: ShapeTextFlow
  layout: ShapeTextLayoutResult
  /** Where to paint each paragraph's fitted drop-cap glyph — empty when nothing in this frame has
   *  an active drop cap. */
  capBoxes: DropCapBox[]
  measureTextPt: (value: string, charIndex?: number) => number
  fonts: ReturnType<typeof useFontsOptional>
  pointSize: number
  /** Row height for a given run — see `runRowMetricPt`. Exposed so the editor's per-chunk strut is
   *  chosen by the same metric that sized the chunk's row. */
  rowMetricPt: RowMetricPt
  familyId: number | undefined
  cssFamily: string
  fontWeight: number
  fontStyle: string
}

export function useShapeTextLayout({
  shape,
  paragraphs,
  isEditing,
  hasNextInChain = false,
  wrap,
}: UseShapeTextLayoutOptions): UseShapeTextLayoutResult {
  const fonts = useFontsOptional()

  const [textValue, setTextValue] = useState(() => paragraphsToPlainText(paragraphs))

  useEffect(() => {
    if (isEditing) return
    setTextValue(paragraphsToPlainText(paragraphs))
  }, [isEditing, paragraphs])

  // Shared with `fitParagraphsToShape` so a linked shape frame measures its capacity with exactly
  // the code path that renders it (WMPCM-7988).
  const measurer = useMemo(
    () => buildShapeTextMeasurer(paragraphs, fonts ?? undefined),
    [paragraphs, fonts]
  )
  const {
    charRunMap,
    measureTextPt,
    pointSize,
    pointPt,
    leadingMult,
    rowMetricPt,
    familyId,
    cssFamily,
    fontWeight,
    fontStyle,
  } = measurer

  const baseFlow = useMemo(
    () => shapeTextFlowWithWrap(shape, getShapeTextFlowBoundary(shape, pointPt, leadingMult), wrap),
    [shape, pointPt, leadingMult, wrap]
  )

  const paragraphAlignments = useMemo(() => paragraphs.map((p) => p.alignment ?? 0), [paragraphs])

  const { flow, layout, capBoxes } = useMemo(() => {
    // Keyed by the LEADED layout each settle pass produced — `layoutShapeTextWithPerLineLeading`
    // picks whichever of its (up to two) settle calls consumed more and returns that pass's
    // `{flow, layout}` by reference, so looking the winner's `layout` back up here recovers the
    // drop-cap flow/capBoxes from the exact pass that won, reclaim retry included.
    const probeFlowByLayout = new Map<ShapeTextLayoutResult, ShapeTextFlow>()
    const capBoxesByLayout = new Map<ShapeTextLayoutResult, DropCapBox[]>()

    const settled = layoutShapeTextWithPerLineLeading(
      shape,
      baseFlow,
      textValue,
      measureTextPt,
      charRunMap,
      paragraphAlignments,
      { breakTrailingWordAtCapacity: hasNextInChain },
      (f) => shapeTextFlowWithWrap(shape, f, wrap),
      (candidate) => {
        const dropCapResult = layoutShapeTextWithDropCap(
          textValue,
          candidate,
          measureTextPt,
          paragraphs,
          charRunMap,
          getSharedMeasureContext(),
          fonts ?? undefined,
          paragraphAlignments,
          { breakTrailingWordAtCapacity: hasNextInChain }
        )
        const { flow: leadedFlow, layout: leadedLayout } = applyPerLineLeadingToFlow(
          dropCapResult.flow,
          dropCapResult.layout,
          charRunMap,
          rowMetricPt
        )

        // Shift yTop by however much per-line leading moved this row, not a full overwrite from the
        // LEADED chunk's own y: a drop cap on any paragraph but the frame's first can have its row's
        // y shifted by per-line leading (`applyPerLineLeadingToFlow` only ever leaves row 0 of the
        // whole frame untouched), and painting at the pre-leading position would put the glyph
        // somewhere the renderer's own chunks are not. But `box.yTop` is not the row's raw top to
        // begin with — it is a precisely solved CSS `top` (top-of-body-text minus the cap's own
        // font-box/ink inset, see `shape-drop-cap.ts`), and overwriting it outright with the leaded
        // chunk's raw `yTop` discarded all of that, painting the glyph back at the row's own top as
        // if `line-height: normal` weren't in play. `box.rowTopY` is the pre-leading raw row top
        // that solve started from, so the delta between it and the leaded chunk's `yTop` is exactly
        // the leading-only shift to apply on top of the already-solved position.
        //
        // `x` is NOT re-derived here, deliberately: per-line leading only remaps vertical position,
        // so a chunk's own x is identical before and after that remap. `box.x` already carries the
        // style-dependent hang-left adjustment `shape-drop-cap.ts` computed for Outside
        // (`chunk.x - indentPt`, versus Inside's plain `chunk.x`), and blindly overwriting it with
        // `chunk.x` here discarded that adjustment, rendering an Outside cap at its row's own
        // indented margin instead of hanging left of it.
        const shiftedCapBoxes = dropCapResult.capBoxes.map((box) => {
          const chunk = leadedLayout.chunks.find((c) => c.startCharIndex === box.charIndex)

          if (!chunk) return box

          const leadingShift = chunk.yTop - box.rowTopY

          return { ...box, yTop: box.yTop + leadingShift, rowTopY: chunk.yTop }
        })

        probeFlowByLayout.set(leadedLayout, dropCapResult.flow)
        capBoxesByLayout.set(leadedLayout, shiftedCapBoxes)

        return { flow: leadedFlow, layout: leadedLayout }
      }
    )

    return {
      // Drop-cap-narrowed but pre-leading — same "every row the shape can offer" contract the
      // return block below documents, just drop-cap aware; reduces to `baseFlow` unchanged whenever
      // nothing in this frame has an active drop cap (`layoutShapeTextWithDropCap`'s own no-op path).
      flow: probeFlowByLayout.get(settled.layout) ?? baseFlow,
      layout: settled.layout,
      capBoxes: capBoxesByLayout.get(settled.layout) ?? [],
    }
  }, [
    baseFlow,
    charRunMap,
    fonts,
    hasNextInChain,
    measureTextPt,
    paragraphAlignments,
    paragraphs,
    rowMetricPt,
    shape,
    textValue,
    wrap,
  ])

  return {
    textValue,
    setTextValue,
    charRunMap,
    // The PROBE grid, deliberately — not `settled.flow`. Its only consumer here is the empty-frame
    // placeholder packer (`shape-text-editor.tsx`), which needs every row the shape can offer, and
    // the settled flow keeps only rows that received a character or a caret: for an empty frame that
    // is the single row holding the initial caret, which would pack "Type here..." into one line.
    //
    // Nothing pairs these rows with `layout.chunks`. The one place that does pair them — the wire
    // baker's exact-`yTop` grouping — runs its own settle call and reads the flow that came back
    // with the chunks, so the two can never be mismatched there.
    flow,
    layout,
    capBoxes,
    measureTextPt,
    fonts,
    pointSize,
    rowMetricPt,
    familyId,
    cssFamily,
    fontWeight,
    fontStyle,
  }
}
